/**
 * A vault file, as any surface needs it.
 *
 * The app models an arc and a recording as Spaces wearing different facets.
 * The web reads FILES, so the entity here is file-shaped — path, front
 * matter, body, the anchors inside it — and every surface derives what it
 * can offer from what the file actually holds. Mirroring the app's runtime
 * model (Space, ContentBlock, typed edges, the packer) would mean building
 * a half-packer in TypeScript that drifts from the real one silently.
 *
 * There is deliberately no `kind` field. A surface asks
 * [capabilitiesOf] what this file can do, never what it is.
 */

import { dayLabel } from '../design/dates';

export interface VaultEntity {
  /** Vault path, e.g. `arcs/Web plane — ….md`. */
  path: string;
  /** Filename without its folder — how membership lists refer to a file. */
  name: string;
  /** The file exactly as stored. Every write starts from THIS, never from
   *  the parsed halves — the machine tail below the body is state the app
   *  reads, and mangling it strands the file. */
  raw: string;
  title: string;
  frontMatter: Record<string, string | string[]>;
  /** Machine state stripped, for display. */
  body: string;
}

/**
 * An arc note carries state a reader should never see: the machine tail, the
 * markers wrapping the body, and inline tag ids.
 *
 * Two tails, because the vault carries both — the one-line `pondering:unit`
 * trailer the app writes since its structure rows moved to
 * `.manifests/<id>.jsonl`, and the base64 manifest that preceded it.
 */
export function readableBody(raw: string): string {
  return (
    raw
      .replace(/<!--pondering:unit [\s\S]*?-->\s*/g, '')
      .replace(/<!--pondering:structure base64[\s\S]*?-->\s*/g, '')
      // A chat turn's opening marker becomes its speaker — stripping it blind
      // would leave the conversation unattributed. Display-only, like every
      // strip here: writes start from `raw` and never see these labels.
      .replace(/^<!--pondering:block [^>]*role:assistant_prompt[^>]*-->[ \t]*$/gm, '**You:**')
      .replace(/^<!--pondering:block [^>]*role:assistant_answer[^>]*-->[ \t]*$/gm, '**Assistant:**')
      // Remaining block wrappers (the arc log's, a placed topic-note's) and
      // member-reference markers are machinery a reader never needs.
      .replace(/^<!--pondering:\/?block[^>]*-->[ \t]*\n?/gm, '')
      .replace(/<!--pondering:\/?content[^>]*-->\n?/g, '')
      .replace(/ ?<!--ref:[^>]*-->/g, '')
      .replace(/ ?<!--tag:[^>]*-->/g, '')
      .trim()
  );
}

/** Headings the export writes as part of a recording's shape. None of them
 *  is a name. */
const STRUCTURAL_HEADINGS = new Set(['face', 'transcript', 'suggestions', 'note', 'members']);

export function parseEntity(path: string, raw: string): VaultEntity {
  const name = path.replace(/^.*\//, '');

  let frontMatterText = '';
  let rest = raw;
  const match = raw.match(/^---\n([\s\S]*?)\n---\n?/);
  if (match) {
    frontMatterText = match[1] ?? '';
    rest = raw.slice(match[0].length);
  }
  const body = readableBody(rest);

  const frontMatter: Record<string, string | string[]> = {};
  let listKey: string | null = null;
  for (const line of frontMatterText.split('\n')) {
    const item = line.match(/^\s+-\s+(.+)$/);
    if (item && listKey) {
      (frontMatter[listKey] as string[]).push(item[1]!.trim());
      continue;
    }
    const pair = line.match(/^([a-zA-Z-]+):\s*(.*)$/);
    if (!pair) continue;
    if (pair[2] === '') {
      listKey = pair[1]!;
      frontMatter[listKey] = [];
    } else {
      frontMatter[pair[1]!] = pair[2]!;
      listKey = null;
    }
  }

  // A file minted in the app has no readable title — it lives in the
  // manifest. The filename is the name there, the same fallback the importer
  // uses for a hand-authored file with no `# Title`.
  //
  // A recording's first heading is `# Face`, which is STRUCTURE, not a name.
  // Taking it would title every recording "Face", so the structural headings
  // are excluded by name and the filename — the capture time — stands in
  // until someone names the thing.
  const heading = body.match(/^#\s+(.+)$/m)?.[1]?.trim();
  const title =
    heading && !STRUCTURAL_HEADINGS.has(heading.toLowerCase())
      ? heading
      : name.replace(/\.md$/, '');

  return { path, name, raw, title, frontMatter, body };
}

/** A front-matter value is a string OR a list — a key with no inline value
 *  becomes an array. Callers must not care which. */
export function listOf(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value;
  return value
    ? String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
}

/** Flattened for display. An array reaching a text slot renders as `a,b,c`
 *  with no spaces, which reads as one broken word. */
export function flat(value: string | string[] | undefined): string {
  return Array.isArray(value) ? value.join(' · ') : (value ?? '');
}

export interface Section {
  anchor: string;
  /** The heading text, or the topic's first line when it has no heading. */
  title: string;
  level: number;
  /** True when the title is a stand-in rather than a name someone gave it. */
  unnamed?: boolean;
}

/**
 * The anchored sections of a body — a recording's topics. They are not
 * separate files: an anchor is a WINDOW into this body, which is why
 * membership can name `path ^anchor` and why a topic has no path of its own.
 *
 * TWO FORMS, and the common one is not the obvious one. Measured across the
 * vault: no recording uses `## Name ^anchor`; 74 of 123 carry a bare
 * `^anchor` on its own line with the topic's paragraphs beneath it. So a
 * topic usually has an identity and NO NAME, and a surface that assumes a
 * title renders nothing at all.
 *
 * Where there is no name, the topic's first line stands in. It is the only
 * honest label available, and it is what a reader would use to recognise the
 * passage anyway.
 */
export function sections(entity: VaultEntity): Section[] {
  const lines = entity.body.split('\n');
  const out: Section[] = [];

  lines.forEach((line, i) => {
    const heading = line.match(/^(#{1,6})\s+(.*?)\s+\^([a-z0-9]{6,})\s*$/i);
    if (heading) {
      out.push({ level: heading[1]!.length, title: heading[2]!, anchor: heading[3]! });
      return;
    }
    const bare = line.match(/^\^([a-z0-9]{6,})\s*$/i);
    if (bare) {
      const firstLine = lines.slice(i + 1).find((l) => l.trim() !== '')?.trim() ?? '';
      out.push({ level: 2, title: firstLine, anchor: bare[1]!, unnamed: true });
    }
  });

  return out;
}

/** Membership refs, stripped to a bare filename. Both `arcs:` (a storyline's
 *  members) and `sources:` (where an arc came from) use the same vocabulary:
 *  a path, optional `^anchors`, optionally wrapped in `[[…]]`. */
export function refNames(value: string | string[] | undefined): string[] {
  return listOf(value)
    .map((s) => s.split(' ^')[0]!.trim().replace(/^\[\[|\]\]$/g, ''))
    .map((p) => p.replace(/^.*\//, ''))
    .filter((p) => p.endsWith('.md'));
}

/**
 * One `## Members` line as the app renders it —
 * `- [label](<path#^anchor>) <!--ref:<id> role:<role>-->`. A `#^anchor` names a
 * topic inside the file, which has no file of its own. `role` is written only
 * where the target does not imply it, so a thread's plain members carry none.
 */
export interface MemberRef {
  id: string;
  path: string;
  anchor: string | null;
  label: string;
  role: string | null;
}

/** What a workspace holds by reference. */
export const SOURCE_ROLE = 'workspace_member';
/** A workspace's talk with the assistant — a file of its own, not a source. */
export const TALK_ROLE = 'assistant_chat';

/** Member lines a page lists in its rail rather than reads out as prose. */
export const LISTED_ROLES: ReadonlySet<string> = new Set([SOURCE_ROLE, TALK_ROLE]);

const MEMBER_REF_LINE = /^- \[(.*?)\]\(<?([^)>]*?)>?\) <!--ref:(\S+?)((?: [^>]*)?)-->[ \t]*$/;

function memberRefOf(line: string): MemberRef | null {
  const m = line.match(MEMBER_REF_LINE);
  if (!m) return null;
  const dest = m[2]!;
  const at = dest.indexOf('#^');
  return {
    id: m[3]!,
    path: at >= 0 ? dest.slice(0, at) : dest,
    anchor: at >= 0 ? dest.slice(at + 2) : null,
    label: m[1]!,
    role: m[4]!.match(/(?:^| )role:(\S+)/)?.[1] ?? null,
  };
}

/** Every member line the file carries, in its order — `FileVaultSerializer.parseMemberRefs`. */
export function memberRefs(raw: string): MemberRef[] {
  return raw
    .split('\n')
    .map(memberRefOf)
    .filter((ref): ref is MemberRef => ref !== null);
}

/**
 * The body without the member lines of [roles], for a page that lists those
 * refs itself — read as prose they are unlinked text. A `## Members` heading
 * left with nothing under it goes with them.
 */
export function bodyWithoutRefs(entity: VaultEntity, roles: ReadonlySet<string>): string {
  const all = entity.raw.split('\n');
  const kept = all.filter((line) => {
    const role = memberRefOf(line)?.role;
    return !role || !roles.has(role);
  });
  if (kept.length === all.length) return entity.body;
  const lines = parseEntity(entity.path, kept.join('\n')).body.split('\n');
  const at = lines.findIndex((line) => /^##\s+members\s*$/i.test(line));
  if (at < 0) return lines.join('\n');
  let end = at + 1;
  while (end < lines.length && lines[end]!.trim() === '') end++;
  if (end < lines.length && !/^#{1,6}\s/.test(lines[end]!)) return lines.join('\n');
  lines.splice(at, end - at);
  return lines.join('\n').trim();
}

export interface BodySection {
  title: string;
  level: number;
  body: string;
}

/**
 * The body split at its headings.
 *
 * A recording is not one blob of prose: the export writes `# Face`,
 * `## Transcript` and `## Suggestions` as real sections, and rendering them
 * as one run of markdown shows a reader the file's plumbing. An arc has no
 * such structure, which is why callers ask for a section by name rather than
 * assuming the shape.
 */
export function bodySections(entity: VaultEntity): BodySection[] {
  const out: BodySection[] = [];
  let current: BodySection | null = null;

  for (const line of entity.body.split('\n')) {
    const heading = line.match(/^(#{1,6})\s+(.+?)\s*$/);
    // A heading carrying an anchor is a TOPIC, not a section — it belongs to
    // whatever section it sits inside.
    if (heading && !/\^[a-z0-9]{6,}$/i.test(heading[2]!)) {
      current = { title: heading[2]!, level: heading[1]!.length, body: '' };
      out.push(current);
      continue;
    }
    if (current) current.body += `${line}\n`;
  }

  return out.map((s) => ({ ...s, body: s.body.trim() }));
}

export function sectionNamed(entity: VaultEntity, title: string): BodySection | null {
  const wanted = title.toLowerCase();
  return bodySections(entity).find((s) => s.title.toLowerCase() === wanted) ?? null;
}

export interface Pick {
  text: string;
  /** The serializer's internal content-block reference. It navigates
   *  NOTHING — never render it as a link. */
  ref: string;
}

/** `- [text](pondering://cb/<id>)` — how the face and the suggestions list
 *  their entries. */
export function picksIn(sectionBody: string): Pick[] {
  const out: Pick[] = [];
  for (const m of sectionBody.matchAll(/^-\s+\[(.+?)\]\((pondering:\/\/cb\/[^)]+)\)\s*$/gm)) {
    out.push({ text: m[1]!, ref: m[2]! });
  }
  return out;
}

export interface Topic extends Section {
  paragraphs: string[];
}

/**
 * The transcript as topics: each anchor line opens one, and the paragraphs
 * beneath it belong to it.
 *
 * Topics are a later pass, so a transcript often has none yet — and a
 * recording short enough never to get one still said something. Everything
 * before the first anchor therefore opens an unnamed topic of its own rather
 * than being dropped, which is what left a short recording rendering an empty
 * transcript section and no stand-in line on its card.
 */
export function transcriptTopics(entity: VaultEntity): Topic[] {
  const transcript = sectionNamed(entity, 'transcript');
  if (!transcript) return [];

  const out: Topic[] = [];
  let current: Topic | null = null;

  for (const line of transcript.body.split('\n')) {
    const heading = line.match(/^#{1,6}\s+(.*?)\s+\^([a-z0-9]{6,})\s*$/i);
    const bare = line.match(/^\^([a-z0-9]{6,})\s*$/i);

    if (heading) {
      current = { anchor: heading[2]!, title: heading[1]!, level: 2, paragraphs: [] };
      out.push(current);
    } else if (bare) {
      current = { anchor: bare[1]!, title: '', level: 2, unnamed: true, paragraphs: [] };
      out.push(current);
    } else if (line.trim()) {
      // Prose ahead of any anchor is the whole transcript on a recording that
      // was never segmented; it belongs to a topic with no id of its own.
      current ??= { anchor: '', title: '', level: 2, unnamed: true, paragraphs: [] };
      if (out.length === 0) out.push(current);
      current.paragraphs.push(line.trim());
    }
  }

  // A nameless topic borrows its first line — the only honest label there is.
  return out.map((t) => (t.unnamed ? { ...t, title: t.paragraphs[0] ?? '' } : t));
}

/** `kind:` as the file declares it — used for LABELS, never for behaviour.
 *  What a surface may do still comes from `capabilitiesOf`; this only says
 *  what to call the thing. */
export function kindOf(entity: VaultEntity): string {
  return flat(entity.frontMatter['kind']) || 'note';
}

/**
 * A deleted unit leaves a marker behind rather than vanishing, so peers that
 * sync later learn about the deletion. It is a real file in the manifest with
 * an empty body — which is exactly how it would render as a blank page if
 * nobody checked.
 */
export function isTombstone(entity: VaultEntity): boolean {
  return kindOf(entity) === 'tombstone' || entity.frontMatter['deleted'] !== undefined;
}

export interface Captured {
  /** `fri 1 aug` */
  day: string;
  /** `01:06` */
  time: string;
}

/**
 * When a recording happened, from its filename.
 *
 * Recordings carry only `id` and `kind` in front matter — no timestamp — but
 * the filename is date-derived and stable. Reading it here beats decoding the
 * ULID, which would be the same information through a harder door.
 */
export function capturedAt(entity: VaultEntity): Captured | null {
  const m = entity.name.match(/(\d{4})-(\d{2})-(\d{2}) at (\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return {
    day: dayLabel(date),
    time: `${hh}:${mm}`,
  };
}

/** True when the name is only the capture time — the file has no name a
 *  person gave it. */
export function isUntitled(entity: VaultEntity): boolean {
  return isStampName(entity.title);
}

/** A name that is only the time a file was made, which nobody gave it. */
export function isStampName(name: string): boolean {
  return /^(?:Recording|Note) \d{4}-\d{2}-\d{2} at \d{2}-\d{2}$/.test(name.trim());
}
